import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { CSSProperties, FC } from 'react';
import { Button, Link, Tooltip } from '@/components/ui';
import { Icon } from '@/components/ui/icon/icon';
import { HeaderNavigationItem } from '@/configs/header/header.config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { resolveStudioPageHref } from '@/utils/resolve-studio-page-href';
import MiniCategoryCard from '../mini-category-card/mini-category-card';
import MiniProductCard from '../mini-product-card/mini-product-card';

const navHoverClassName =
  'transition-colors hover:![color:var(--nav-hover-color)] data-[hover=true]:![color:var(--nav-hover-color)]';

const dropdownTriggerClassName = cn(
  'z-20 inline-flex items-center gap-1 bg-transparent px-3 py-2 text-sm font-medium shadow-none hover:bg-transparent',
  navHoverClassName,
);

const navItemClassName = (extra?: string) =>
  cn('px-3 py-2 text-sm font-medium', navHoverClassName, extra);

const dropdownButtonClassName = cn(
  'z-20 flex items-center gap-1 bg-transparent px-3 py-2 text-sm shadow-none hover:bg-transparent',
  navHoverClassName,
);

const NavigationBarV1: FC = () => {
  const resolveThemeColor = useResolveThemeColor();
  const headerContent = useHeaderContent();
  const navigationTextColor = resolveThemeColor(headerContent.navigationTextColor ?? 'foreground');
  const navigationHoverTextColor = resolveThemeColor(
    headerContent.navigationHoverTextColor ?? 'primary',
  );

  const navItemStyle = {
    color: navigationTextColor,
    '--nav-hover-color': navigationHoverTextColor,
  } as CSSProperties;

  const renderNavigationItem = (item: HeaderNavigationItem) => {
    if (item.type === 'link') {
      return (
        <Link
          href={resolveStudioPageHref(item.pageUrl)}
          className={navItemClassName()}
          noStyle
          style={navItemStyle}
        >
          {item.label}
        </Link>
      );
    }

    if (item.type === 'category-link') {
      const firstCategory = (item.categories ?? []).find(category => category);
      const href = firstCategory?.meta?.url ? `/products/${firstCategory.meta.url}` : '/products';
      return (
        <Link href={href} className={navItemClassName()} noStyle style={navItemStyle}>
          {item.label || firstCategory?.name || 'Category'}
        </Link>
      );
    }

    if (item.type === 'categories-dropdown') {
      const triggerHref = item.triggerButtonUrl
        ? resolveStudioPageHref(item.triggerButtonUrl)
        : undefined;
      return (
        <Tooltip
          placement="bottom"
          keepContentMounted
          classNames={{ content: 'max-w-[520px] @lg:max-w-[720px] p-0' }}
          delay={400}
          content={
            <div className="z-10 flex max-h-[70dvh] scrollbar-thin overflow-y-auto p-3">
              <div className="grid w-full grid-cols-2 gap-2 @lg:grid-cols-3">
                {(item.categories ?? [])
                  .filter(category => category)
                  .map(category => (
                    <MiniCategoryCard
                      key={category!.id}
                      name={category!.name}
                      metaUrl={category!.meta.url}
                      image={category!.meta.thumbnail}
                    />
                  ))}
              </div>
            </div>
          }
        >
          {triggerHref ? (
            <Link
              href={triggerHref}
              noStyle
              className={dropdownTriggerClassName}
              style={navItemStyle}
            >
              {item.label}
              <Icon icon={faChevronDown} className="h-3 w-3" />
            </Link>
          ) : (
            <Button
              variant="light"
              size="md"
              className={dropdownButtonClassName}
              style={navItemStyle}
            >
              {item.label}
              <Icon icon={faChevronDown} className="h-3 w-3" />
            </Button>
          )}
        </Tooltip>
      );
    }

    return (
      <Tooltip
        placement="bottom"
        keepContentMounted
        classNames={{ content: 'max-w-[520px] @lg:max-w-[720px] p-0' }}
        delay={400}
        content={
          <div className="z-10 flex max-h-[70dvh] scrollbar-thin overflow-y-auto p-3">
            <div className="grid w-full grid-cols-2 gap-2 @lg:grid-cols-3">
              {(item.products ?? [])
                .filter(product => product)
                .map(product => (
                  <MiniProductCard
                    locale={product!.locale}
                    key={product!.id}
                    name={product!.name}
                    metaUrl={product!.meta.url}
                    image={product!.images[0]}
                    price={product!.price}
                    discountedPrice={product!.discountedPrice}
                    superCategories={product!.superCategories}
                    productId={product!.id}
                  />
                ))}
            </div>
          </div>
        }
      >
        {item.triggerButtonUrl ? (
          <Link
            href={resolveStudioPageHref(item.triggerButtonUrl)}
            noStyle
            className={dropdownTriggerClassName}
            style={navItemStyle}
          >
            {item.label}
            <Icon icon={faChevronDown} className="h-3 w-3" />
          </Link>
        ) : (
          <Button
            variant="light"
            size="md"
            className={dropdownButtonClassName}
            style={navItemStyle}
          >
            {item.label}
            <Icon icon={faChevronDown} className="h-3 w-3" />
          </Button>
        )}
      </Tooltip>
    );
  };

  return (
    <div className="z-50 hidden px-4 @lg:block">
      <div className="flex justify-center">
        {headerContent.navigationItems.map(item => (
          <div key={`${item.type}-${item.label}`} className="flex items-center">
            {renderNavigationItem(item)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NavigationBarV1;
